import { Block, PrintDocument } from '../document'

/**
 * Templates are JSON (POS.md §18.9 §4): blocks whose strings hold `{{path}}` fields, plus two control keys any block
 * may carry — `if` (render only when the path is truthy) and, on a `repeat` block, `each` (render `blocks` once per
 * item, with the item as `it`). The future visual editor writes the same JSON.
 */
export type TemplateBlock =
  | (Block & { if?: string })
  | { type: 'repeat'; each: string; blocks: TemplateBlock[]; if?: string }

export type Template =
  | { kind: 'roll'; blocks: TemplateBlock[]; cut?: boolean }
  | { kind: 'label'; blocks: TemplateBlock[] }

type Data = Record<string, unknown>

const get = (data: Data, path: string): unknown =>
  path.split('.').reduce<unknown>((v, key) => (v && typeof v === 'object' ? (v as Data)[key] : undefined), data)

const truthy = (v: unknown) => (Array.isArray(v) ? v.length > 0 : !!v)

const interpolate = (s: string, data: Data) =>
  s.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, path: string) => {
    const v = get(data, path)
    return v === undefined || v === null ? '' : String(v)
  })

const fillBlocks = (blocks: TemplateBlock[], data: Data): Block[] =>
  blocks.flatMap((tb): Block[] => {
    if (tb.if && !truthy(get(data, tb.if))) return []
    if (tb.type === 'repeat') {
      const items = get(data, tb.each)
      return Array.isArray(items) ? items.flatMap((it) => fillBlocks(tb.blocks, { ...data, it })) : []
    }
    const { if: _if, ...block } = tb as Block & { if?: string }
    const out: Record<string, unknown> = { ...block }
    for (const [k, v] of Object.entries(out)) if (typeof v === 'string' && k !== 'type') out[k] = interpolate(v, data)
    const b = out as Block
    // A barcode or text whose field came out empty is dropped rather than printed blank.
    if ((b.type === 'barcode' || b.type === 'qr') && !b.value) return []
    if (b.type === 'image' && !b.src) return []
    return [b]
  })

export const fillTemplate = (template: Template, data: Data, extra: { drawer?: boolean; copies?: number } = {}): PrintDocument =>
  template.kind === 'roll'
    ? { kind: 'roll', blocks: fillBlocks(template.blocks, data), cut: template.cut, drawer: extra.drawer }
    : { kind: 'label', blocks: fillBlocks(template.blocks, data), copies: extra.copies }

/** Several labels on A4 (label sheets, cards): one filled template per cell. */
export const fillSheet = (
  template: Template,
  items: Data[],
  layout: Extract<PrintDocument, { kind: 'sheet' }>['layout']
): PrintDocument => ({ kind: 'sheet', layout, cells: items.map((data) => fillBlocks(template.blocks, data)) })
